//src/pages/partner/PartnerInsuranceRequestDetail.jsx
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { supabase } from "../../lib/supabaseClient";

function fmtDate(d) {
  if (!d) return "";
  try {
    return new Date(d).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return String(d);
  }
}

function badgeClass(status) {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "bg-amber-100 text-amber-800 border-amber-200";
  if (s === "seen") return "bg-blue-100 text-blue-800 border-blue-200";
  if (s === "answered") return "bg-emerald-100 text-emerald-800 border-emerald-200";
  if (s === "quoted") return "bg-purple-100 text-purple-800 border-purple-200";
  if (s === "accepted") return "bg-green-100 text-green-800 border-green-200";
  if (s === "rejected") return "bg-red-100 text-red-800 border-red-200";
  if (s === "done") return "bg-gray-100 text-gray-700 border-gray-200";
  return "bg-gray-100 text-gray-700 border-gray-200";
}

// Une fois l'un de ces statuts atteint, l'expert ne doit plus pouvoir faire
// régresser la demande (le client a déjà décidé, ou le dossier est clos).
const LOCKED_STATUSES = ["accepted", "rejected", "done"];

export default function PartnerInsuranceRequestDetail() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [row, setRow] = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [actionInProgress, setActionInProgress] = useState(""); // "seen", "answered", "quoted", "done"

  const [message, setMessage] = useState("");
  const [quoteAmount, setQuoteAmount] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setErr("");

      const { data, error } = await supabase
        .from("insurance_requests")
        .select(
          "id,created_at,updated_at,status,request_type,insured_value,currency,notes,payload,partner_message,quote_amount,quote_currency,seen_at,answered_at,closed_at,coverage_context,policy_kind,transport_needed,event_start_date,event_end_date"
        )
        .eq("id", id)
        .single();

      if (error) throw error;

      setRow(data);
      setMessage(data.partner_message || "");
      setQuoteAmount(
        data.quote_amount !== null && data.quote_amount !== undefined ? String(data.quote_amount) : ""
      );
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Erreur chargement demande");
      setRow(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 4000);
  };

  // Vérifie l'état le plus frais possible (évite de se fier à une fermeture obsolète)
  const isRequestLocked = () => {
    const s = String(row?.status || "").toLowerCase();
    return LOCKED_STATUSES.includes(s);
  };

  const update = async (patch, action, successMsg) => {
    if (isRequestLocked()) {
      setErr("Cette demande est déjà clôturée ou a reçu une décision du client : plus aucune modification n'est possible.");
      return;
    }

    try {
      setSaving(true);
      setActionInProgress(action);
      setErr("");

      const { error } = await supabase.from("insurance_requests").update(patch).eq("id", id);
      if (error) throw error;

      await load();
      showSuccess(successMsg);
    } catch (e) {
      console.error(e);
      setErr(e?.message || "Impossible de mettre à jour la demande");
    } finally {
      setSaving(false);
      setActionInProgress("");
    }
  };

  const markSeen = async () => {
    await update(
      { status: "seen", seen_at: new Date().toISOString() },
      "seen",
      "✓ Demande marquée comme vue"
    );
  };

  const sendAnswer = async () => {
    await update(
      {
        status: "answered",
        partner_message: message || null,
        answered_at: new Date().toISOString(),
      },
      "answered",
      "✓ Message envoyé au collectionneur"
    );
  };

  const sendQuote = async () => {
    const q = quoteAmount === "" ? null : Number(String(quoteAmount).replace(",", "."));
    if (q !== null && Number.isNaN(q)) {
      setErr("Montant de devis invalide");
      return;
    }

    await update(
      {
        status: "quoted",
        partner_message: message || null,
        quote_amount: q,
        quote_currency: "EUR",
        answered_at: new Date().toISOString(),
      },
      "quoted",
      "✓ Devis envoyé avec succès"
    );
  };

  const close = async () => {
    await update(
      { status: "done", closed_at: new Date().toISOString() },
      "done",
      "✓ Demande clôturée"
    );
  };

  if (loading) return <div className="p-8 text-sm text-gray-500">Chargement…</div>;

  if (err && !row) {
    return (
      <div className="p-8 space-y-4">
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50"
        >
          Retour
        </button>
      </div>
    );
  }

  if (!row) return null;

  const p = row.payload || {};
  const a = p.artwork || {};
  const client = p.client || {};
  const title = a.title || p.artwork_title || "Œuvre";
  const artist = a.artist || p.artwork_artist || "";
  const locked = isRequestLocked();

  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto space-y-6">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
        <div className="text-2xl font-serif font-semibold">Demande Assurance</div>
        <div className="text-gray-600 mt-1">Traitement partenaire</div>

        <div className="mt-4 flex items-center gap-3">
          <span className={`text-xs px-2 py-1 rounded-lg border ${badgeClass(row.status)}`}>
            {row.status}
          </span>
          <span className="text-xs text-gray-500">Créée: {fmtDate(row.created_at)}</span>
        </div>
      </div>

      {successMessage && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-3 animate-[fadeIn_0.3s_ease-in]">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span className="font-medium">{successMessage}</span>
        </div>
      )}

      {locked && (
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
          🔒 Cette demande est au statut <strong>{row.status}</strong>. Le client a déjà pris une décision ou le
          dossier est clôturé : aucune action supplémentaire n'est possible ici.
        </div>
      )}

      <div className="rounded-2xl border border-gray-200 bg-white p-5 text-sm text-gray-700">
        <div className="font-medium text-gray-900">🔒 Cadre d'intervention</div>
        <div className="mt-2">
          Les informations affichées ci-dessous ont été partagées par le collectionneur dans le cadre de cette demande d'assurance.
          Elles sont limitées à l'analyse assurantielle et ne donnent pas accès à l'ensemble de sa collection.
        </div>
      </div>

      {err ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{err}</div>
      ) : null}

      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
        <div><strong>Contexte :</strong>{" "}
          {row.coverage_context === "home_premium"
            ? "Assurance habitation haut de gamme"
            : row.coverage_context === "fine_art_policy"
            ? "Contrat spécifique œuvres d'art"
            : "—"}
        </div>

        {row.policy_kind && (
          <div><strong>Type :</strong>{" "}
            {row.policy_kind === "nail_to_nail" ? "Clou à clou" : "Tous risques"}
          </div>
        )}

        {row.transport_needed && <div>🚚 Transport requis</div>}

        {row.event_start_date && (
          <div>📅 Événement :{" "}
            {row.event_start_date} → {row.event_end_date || "—"}
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-3">
        <div className="text-lg font-medium text-gray-900">
          {title} {artist ? <span className="text-gray-500">• {artist}</span> : null}
        </div>
        <div className="text-xs text-gray-500">Données fournies pour analyse assurantielle.</div>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <span className="text-gray-500">Type:</span> {row.request_type}
          </div>
          <div>
            <span className="text-gray-500">Valeur demandée:</span>{" "}
            {row.insured_value !== null && row.insured_value !== undefined
              ? `${row.insured_value} ${row.currency || "EUR"}`
              : "—"}
          </div>
          <div>
            <span className="text-gray-500">Client:</span> {client.name || "—"}
          </div>
          <div>
            <span className="text-gray-500">Email:</span> {client.email || "—"}
          </div>
        </div>

        {p.context || row.notes ? (
          <div className="text-sm text-gray-700">
            <div className="text-gray-500 font-medium mb-1">Message client</div>
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 whitespace-pre-wrap">
              {p.context || row.notes}
            </div>
          </div>
        ) : null}
      </div>

      {!locked && (
        <div className="rounded-2xl border border-gray-200 bg-white p-6 space-y-4">
          <div className="text-lg font-medium text-gray-900">Répondre</div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Message au collectionneur</label>
            <textarea
              className="mt-1 w-full rounded-xl border border-gray-200 p-2 min-h-[140px]"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Questions, précisions requises, conditions de couverture, délai…"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Montant du devis (EUR)</label>
              <input
                className="mt-1 w-full rounded-xl border border-gray-200 p-2"
                value={quoteAmount}
                onChange={(e) => setQuoteAmount(e.target.value)}
                placeholder="ex: 420"
                inputMode="decimal"
              />
              <div className="text-xs text-gray-500 mt-1">
                Laisse vide si tu envoies seulement un message.
              </div>
            </div>

            <div className="flex flex-col justify-end gap-2">
              {row.status === "pending" && (
                <button
                  disabled={saving}
                  onClick={markSeen}
                  className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {actionInProgress === "seen" ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>Traitement...</span>
                    </>
                  ) : (
                    "Marquer comme vue"
                  )}
                </button>
              )}

              <button
                disabled={saving}
                onClick={sendAnswer}
                className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actionInProgress === "answered" ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Envoi...</span>
                  </>
                ) : (
                  "Envoyer message (status=answered)"
                )}
              </button>

              <button
                disabled={saving}
                onClick={sendQuote}
                className="px-4 py-2 rounded-xl bg-black text-white font-medium hover:bg-gray-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actionInProgress === "quoted" ? (
                  <>
                    <svg className="animate-spin h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Envoi...</span>
                  </>
                ) : (
                  "Envoyer devis (status=quoted)"
                )}
              </button>

              <button
                disabled={saving}
                onClick={close}
                className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {actionInProgress === "done" ? (
                  <>
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span>Clôture...</span>
                  </>
                ) : (
                  "Clôturer (done)"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50"
        >
          Retour
        </button>
        <button
          onClick={load}
          className="px-4 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50"
        >
          Rafraîchir
        </button>
      </div>
    </div>
  );
}